/**
 * Transcript scroll policy — pure math and tiny data structures for the
 * pinned-to-bottom scroll model shared by every chat surface (GlobalChatSystem,
 * the landing-page demo chat, the Hidalgo widget, Moonshot voice chat).
 *
 * No DOM access in this module: everything operates on plain numbers and ids
 * so it can be unit-tested without jsdom geometry stubs, mirroring
 * components/GlobalChatSystem/threadNav.ts. The stateful controller that owns
 * the DOM lives in src/hooks/useTranscriptScroll.ts.
 */

/**
 * Distance-from-bottom band (px) inside which the viewport counts as "at the
 * bottom" and stays pinned. Deliberately small: the old 600px threshold
 * treated "scrolled up half a screen, reading" as at-bottom and yanked the
 * user down on every content update.
 */
export const BOTTOM_PIN_BAND_PX = 120;

/** Distance from the top (px) that counts as "near top" (shows the previous-X pill). */
export const NEAR_TOP_THRESHOLD_PX = 150;

/** How long a saved per-thread reading position stays restorable. */
export const THREAD_SCROLL_MEMORY_TTL_MS = 30 * 60 * 1000;

/** Max remembered threads before the oldest entry is evicted. */
export const THREAD_SCROLL_MEMORY_CAP = 50;

/**
 * Window after a thread switch during which unpinned append/edit changes are
 * absorbed silently (server hydration lands right after a switch and must not
 * light a false "N new" badge).
 */
export const POST_SWITCH_SETTLE_MS = 1500;

/** Downward finger travel (px) that counts as an intentional scroll-up on touch. */
export const TOUCH_UNPIN_SLOP_PX = 8;

/** Programmatic smooth scrolls count as arrived within this many px of target. */
export const PROGRAMMATIC_ARRIVAL_EPSILON_PX = 4;

/** Safety timeout for programmatic smooth-scroll tracking (interrupted animations). */
export const PROGRAMMATIC_SCROLL_TIMEOUT_MS = 700;

export function distanceFromBottom(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): number {
  return Math.max(0, scrollHeight - scrollTop - clientHeight);
}

export function isWithinBottomBand(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
  bandPx: number = BOTTOM_PIN_BAND_PX,
): boolean {
  return distanceFromBottom(scrollTop, scrollHeight, clientHeight) <= bandPx;
}

export function isNearTranscriptTop(
  scrollTop: number,
  thresholdPx: number = NEAR_TOP_THRESHOLD_PX,
): boolean {
  return scrollTop < thresholdPx;
}

// ────────────────────────────────────────────────────────────────────────────
// Items-change classification
// ────────────────────────────────────────────────────────────────────────────

/** Cheap fingerprint of a transcript's item list. */
export interface TranscriptItemsShape {
  firstId: string | null;
  lastId: string | null;
  count: number;
}

export function shapeOfItems(
  items: ReadonlyArray<{ id?: string | null }>,
): TranscriptItemsShape {
  return {
    firstId: items.length ? (items[0].id ?? null) : null,
    lastId: items.length ? (items[items.length - 1].id ?? null) : null,
    count: items.length,
  };
}

/**
 * - `append`  — items grew at the tail (new message). The only kind that may
 *               increment the "N new" badge while unpinned.
 * - `prepend` — items grew at the head with the tail unchanged (load-earlier
 *               page). The surface's own anchor effect owns the scroll
 *               correction; the controller must stay inert and never badge.
 * - `edit`    — same ends, same count: in-place content mutation (streaming
 *               update, attachment patch).
 * - `replace` — anything else (clear chat, wholesale rebuild).
 * - `none`    — identical shape references aside, nothing measurable changed.
 */
export type TranscriptChangeKind = 'none' | 'append' | 'prepend' | 'edit' | 'replace';

export function classifyItemsChange(
  prev: TranscriptItemsShape,
  next: TranscriptItemsShape,
): TranscriptChangeKind {
  if (
    prev.count === next.count
    && prev.firstId === next.firstId
    && prev.lastId === next.lastId
  ) {
    // The caller only classifies when the items identity changed, so an
    // unchanged shape means an in-place mutation of some item's content.
    return prev.count === 0 ? 'none' : 'edit';
  }
  if (next.count > prev.count) {
    if (prev.count === 0) return 'append';
    if (prev.firstId === next.firstId && prev.lastId !== next.lastId) return 'append';
    if (prev.lastId === next.lastId && prev.firstId !== next.firstId) return 'prepend';
    return 'replace';
  }
  return 'replace';
}

// ────────────────────────────────────────────────────────────────────────────
// Per-thread scroll memory ("pick up where you left off" within a session)
// ────────────────────────────────────────────────────────────────────────────

export interface ThreadScrollMemoryEntry {
  scrollTop: number;
  pinned: boolean;
  savedAt: number;
}

export type ThreadScrollMemory = Map<string, ThreadScrollMemoryEntry>;

export function recordThreadScroll(
  memory: ThreadScrollMemory,
  key: string,
  entry: ThreadScrollMemoryEntry,
  cap: number = THREAD_SCROLL_MEMORY_CAP,
): void {
  memory.set(key, entry);
  while (memory.size > cap) {
    let oldestKey: string | null = null;
    let oldestAt = Infinity;
    memory.forEach((value, mapKey) => {
      if (value.savedAt < oldestAt) {
        oldestAt = value.savedAt;
        oldestKey = mapKey;
      }
    });
    if (oldestKey === null) break;
    memory.delete(oldestKey);
  }
}

/**
 * Returns the remembered position for `key` if it is still fresh, dropping
 * (and returning null for) stale entries. In-memory only by design: a page
 * reload always starts at the bottom of the active thread — that IS the
 * "coming back" policy — and no cross-surface localStorage key exists to
 * clobber.
 */
export function lookupFreshThreadScroll(
  memory: ThreadScrollMemory,
  key: string,
  now: number,
  ttlMs: number = THREAD_SCROLL_MEMORY_TTL_MS,
): ThreadScrollMemoryEntry | null {
  const entry = memory.get(key);
  if (!entry) return null;
  if (now - entry.savedAt > ttlMs) {
    memory.delete(key);
    return null;
  }
  return entry;
}
